import { db } from "@db";
import { cacheUser, clearCachedUser } from "@db/cachedUser";
import { useOnlineStatus } from "@hooks/use-online-status";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { isPreviewBuild, PREVIEW_USER } from "@lib/preview";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect } from "react";

export type CurrentUser = {
  id: string;
  email: string;
  initials: string;
  name: string;
  picture?: string;
};

function getInitials(name: string, email: string): string {
  const source = name || email;
  return source
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

type CurrentUserResult = {
  user: CurrentUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isOffline: boolean;
  isCachedIdentity: boolean;
};

/**
 * Return the current user's ID, falling back to "local" when unauthenticated.
 */
export function useOwnerId(): string {
  const { user } = useCurrentUser();
  return user?.id ?? "local";
}

/**
 * Return the current user from Kinde auth, with offline/cached identity support.
 */
export function useCurrentUser(): CurrentUserResult {
  const { user: kindeUser, isLoading, isAuthenticated } = useKindeAuth();
  const isOnline = useOnlineStatus();
  const cachedUser = useLiveQuery(() => db.cachedUser.get("cachedUser"));
  const isPreview = isPreviewBuild();

  useEffect(() => {
    if (isPreview) {
      return;
    }

    if (isAuthenticated && kindeUser) {
      cacheUser({
        userId: kindeUser.id,
        email: kindeUser.email ?? "",
        name: [kindeUser.givenName, kindeUser.familyName]
          .filter(Boolean)
          .join(" "),
      });
    }
  }, [isPreview, isAuthenticated, kindeUser]);

  useEffect(() => {
    if (isPreview) {
      return;
    }

    if (isOnline && !isLoading && !isAuthenticated) {
      clearCachedUser();
    }
  }, [isPreview, isOnline, isLoading, isAuthenticated]);

  if (isPreview) {
    return {
      user: PREVIEW_USER,
      isLoading: false,
      isAuthenticated: true,
      isOffline: !isOnline,
      isCachedIdentity: false,
    };
  }

  if (isAuthenticated && kindeUser) {
    const email = kindeUser.email ?? "";
    const name = [kindeUser.givenName, kindeUser.familyName]
      .filter(Boolean)
      .join(" ");

    return {
      user: {
        id: kindeUser.id,
        email,
        initials: getInitials(name, email),
        name,
        picture: kindeUser.picture ?? undefined,
      },
      isLoading: false,
      isAuthenticated: true,
      isOffline: !isOnline,
      isCachedIdentity: false,
    };
  }

  if (!isOnline && cachedUser) {
    return {
      user: {
        id: cachedUser.userId,
        email: cachedUser.email,
        initials: getInitials(cachedUser.name, cachedUser.email),
        name: cachedUser.name,
      },
      isLoading: false,
      isAuthenticated: false,
      isOffline: true,
      isCachedIdentity: true,
    };
  }

  if (isLoading && cachedUser) {
    return {
      user: {
        id: cachedUser.userId,
        email: cachedUser.email,
        initials: getInitials(cachedUser.name, cachedUser.email),
        name: cachedUser.name,
      },
      isLoading: true,
      isAuthenticated: false,
      isOffline: !isOnline,
      isCachedIdentity: true,
    };
  }

  return {
    user: null,
    isLoading,
    isAuthenticated: false,
    isOffline: !isOnline,
    isCachedIdentity: false,
  };
}
